/**
 * @file movements-summary.ts
 * @description Итог по отбору журнала движений: приход, расход, сальдо
 * (phase19.24 этап 1, шаг 2).
 *
 * Вопрос кладовщика к журналу — не «какие были строки», а «сколько пришло и
 * сколько ушло». Считаем по ВИДИМЫМ строкам (после всех фильтров): итог должен
 * сходиться с тем, что человек видит на экране, иначе он не сверит его глазами.
 *
 * Decimal с сервера приходит строкой; складываем числами и округляем до трёх
 * знаков — сумма `0.1 + 0.2` в двоичной плавающей точке даёт хвост
 * `0.30000000000000004`, и итог показал бы мусор.
 */

import { formatQtyValue } from "./picking-format";

/**
 * Подпись единицы к итогу: `« шт»` или пустая строка.
 *
 * В журнале лежат движения разных фасовок (50 г, 100 г, кг), и «шт» под общей
 * суммой врало: 3 пакета по 50 г и 2 кг — это не «5 шт» (находка ревью
 * 2026-09-20). Единица имеет смысл только когда в видимых строках одна
 * фасовка; самой единицы в контракте движений нет, поэтому пишем складское
 * «шт» — количество там считается в единицах учёта фасовки.
 */
export function movementsUnitSuffix(items: { skuId: string }[]): string {
  const skus = new Set(items.map((item) => item.skuId));
  return skus.size === 1 ? " шт" : "";
}

/** Итог по отбору. `outcome` отрицательный — знак несёт само число. */
export interface MovementsSummary {
  /** Сумма положительных движений (приход). */
  income: number;
  /** Сумма отрицательных движений (расход), число ≤ 0. */
  outcome: number;
  /** Приход + расход. */
  balance: number;
}

/** Шкала количеств склада — три знака после запятой (кг, л). */
function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}

/** Приход / расход / сальдо по видимым строкам журнала. */
export function summarizeMovements(
  items: { quantity: string }[],
): MovementsSummary {
  let income = 0;
  let outcome = 0;
  for (const item of items) {
    const value = Number(item.quantity);
    // Битое значение не роняет итог и не превращает его в NaN: строка с
    // нечитаемым количеством просто не участвует в сумме.
    if (!Number.isFinite(value)) continue;
    if (value >= 0) income += value;
    else outcome += value;
  }
  const rounded = { income: round3(income), outcome: round3(outcome) };
  return { ...rounded, balance: round3(rounded.income + rounded.outcome) };
}

/**
 * Количество со знаком для плиток итога: «+12», «−3», «0».
 *
 * Минус берём типографский (U+2212): в колонке `tabular-nums` дефис заметно
 * короче и итог выглядит съехавшим.
 */
export function formatSignedQty(value: number): string {
  if (value > 0) return `+${formatQtyValue(value)}`;
  if (value < 0) return `−${formatQtyValue(Math.abs(value))}`;
  return formatQtyValue(value);
}
